import React, { useState } from "react";
import { BarChart3, Users, TrendingUp, ArrowRight, CheckCircle2, X, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context";
import { Logo } from "@/components/Logo";

export function LandingPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const openLogin = () => { setShowLogin(true); };

  const services = [
    { icon: BarChart3, title: "Strategic Analytics", desc: "Data-driven insights that transform raw numbers into actionable marketing strategies for measurable growth." },
    { icon: Users, title: "Brand Consulting", desc: "Build brand equity through positioning, messaging, and identity systems that resonate with your audience." },
    { icon: TrendingUp, title: "Growth Marketing", desc: "Full-funnel optimization from acquisition to retention, powered by performance analytics and creative execution." },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2" data-testid="landing-logo">
            <Logo size={32} wordClassName="text-xl" />
          </div>
          <nav className="hidden md:flex items-center gap-10">
            <a href="#services" className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors">Services</a>
            <a href="#about" className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors">About</a>
            <a href="#results" className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors">Results</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Button onClick={openLogin} className="text-sm font-medium rounded-full px-5 bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-all hover:scale-105" data-testid="header-login-btn">Log In</Button>
          </div>
          <button className="md:hidden" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
            {mobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden border-t border-zinc-100 bg-white px-6 py-4 space-y-3">
            <a href="#services" className="block text-sm font-medium text-zinc-600">Services</a>
            <a href="#about" className="block text-sm font-medium text-zinc-600">About</a>
            <a href="#results" className="block text-sm font-medium text-zinc-600">Results</a>
            <div className="flex gap-3 pt-2">
              <Button onClick={openLogin} className="text-sm rounded-full px-5 bg-[var(--color-primary)] text-white">Log In</Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="pt-32 pb-24 lg:pt-44 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-100 text-xs font-medium text-zinc-600 mb-8 tracking-wide uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary-hover)]" />
                Marketing Consulting
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Turn data into
                <br />
                <span className="text-[var(--color-primary-hover)]">growth.</span>
              </h1>
              <p className="text-lg text-zinc-500 leading-relaxed mb-10 max-w-lg">
                We help businesses unlock their marketing potential through strategic analytics, brand positioning, and performance-driven campaigns.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={openLogin} className="rounded-full px-8 py-6 text-base font-medium bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-all hover:scale-105 group" data-testid="hero-cta-btn">
                  Client Login <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-100">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" alt="Data analytics dashboard" className="w-full h-full object-cover" loading="eager" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-medium tracking-widest uppercase text-[var(--color-primary-hover)]">What We Do</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-4 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Consulting that drives results
            </h2>
            <p className="text-zinc-500 leading-relaxed">
              From strategy to execution, we partner with you at every stage to deliver marketing outcomes that matter.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((svc, i) => (
              <div key={svc.title} className="group border border-zinc-200 p-8 hover:border-zinc-900 transition-all duration-300 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid={`service-card-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center mb-6 group-hover:bg-[var(--color-primary-hover)] group-hover:text-white transition-colors">
                  <svc.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold tracking-tight mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{svc.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-24 lg:py-32 bg-zinc-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-200">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80" alt="Team collaboration" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <span className="text-xs font-medium tracking-widest uppercase text-[var(--color-primary-hover)]">About Us</span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-4 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Your growth is our mission
              </h2>
              <p className="text-zinc-500 leading-relaxed mb-6">
                ANALYTICAY was founded on a simple belief: every business deserves access to world-class marketing strategy. We combine deep analytical expertise with creative vision to deliver consulting that doesn't just advise — it transforms.
              </p>
              <div className="space-y-4">
                {["Data-first approach to every engagement", "Cross-industry marketing expertise", "Dedicated team for each client partnership"].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--color-primary-hover)] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-zinc-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results CTA */}
      <section id="results" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Ready to accelerate your growth?
          </h2>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto mb-10">
            Join the companies that trust ANALYTICAY to drive their marketing strategy and deliver measurable results.
          </p>
          <Button onClick={openLogin} className="rounded-full px-10 py-6 text-base font-medium bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-all hover:scale-105 group" data-testid="cta-login-btn">
            Client Login <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-100 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size={20} wordClassName="text-sm" />
          <p className="text-xs text-zinc-400">&copy; {new Date().getFullYear()} analyticaY. All rights reserved.</p>
        </div>
      </footer>

      {/* Login Modal */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="sm:max-w-md rounded-2xl border-zinc-200 p-0 overflow-hidden">
          <div className="p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Welcome Back
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                Sign in to your ANALYTICAY account
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-lg border-zinc-200 h-11" required data-testid="login-email-input" />
              <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-lg border-zinc-200 h-11" required minLength={6} data-testid="login-password-input" />
              <Button type="submit" disabled={isLoading} className="w-full rounded-lg h-11 bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition-all font-medium" data-testid="login-submit-btn">
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
